import { useCallback, useEffect, useMemo } from 'react';

import { inspectionApi } from '../features/inspections/inspectionAPI';
import type { InspectionDamage } from '../features/inspections/inspectionTypes';
import { isDamageDeleted } from '../features/inspections/utils/damageAnnotationUtils';
import { MOBILE_VIEW_ALIASES, MOBILE_VIEW_NAME } from '../features/inspections/inspectionViewConstants';

type UseDamagesParams = {
  inspectionId: string;
  enabled?: boolean;
  viewName?: string;
  setDamage?: (val: number) => void;
};

const EMPTY_DAMAGES: InspectionDamage[] = [];

export const useDamages = ({ inspectionId, enabled = true, viewName, setDamage }: UseDamagesParams) => {
  const { data: queriedDamages, isLoading, isFetching, error } = inspectionApi.useGetDamagesQuery(inspectionId, {
    skip: !inspectionId || !enabled,
  });

  const { data: cachedDamages } = inspectionApi.endpoints.getDamages.useQueryState(inspectionId, {
    skip: !inspectionId,
  });

  const [triggerFetchDamages, { isFetching: isManualFetching }] = inspectionApi.useLazyGetDamagesQuery();

  const damages = enabled ? queriedDamages ?? EMPTY_DAMAGES : cachedDamages ?? EMPTY_DAMAGES;

  // Soft-deleted rows stay in GET /damages on on-prem; hide them from UI lists.
  // AnnotationHistory reads the raw query so deleted history still appears.
  const activeDamages = useMemo(() => damages.filter(damage => !isDamageDeleted(damage)), [damages]);

  const fetchDamages = useCallback(async () => {
    if (!inspectionId) return [] as InspectionDamage[];

    const nextDamages = await triggerFetchDamages(inspectionId).unwrap();
    return nextDamages.filter(damage => !isDamageDeleted(damage));
  }, [inspectionId, triggerFetchDamages]);

  const filteredDamages = useMemo(() => {
    if (!viewName) {
      return activeDamages;
    }

    return activeDamages.filter(
      item =>
        (item.viewName === viewName ||
          (viewName === MOBILE_VIEW_NAME && (MOBILE_VIEW_ALIASES as readonly string[]).includes(item.viewName || ''))) &&
        Boolean(item.newMetadata || item.metadata)
    );
  }, [activeDamages, viewName]);

  useEffect(() => {
    setDamage?.(activeDamages.length);
  }, [activeDamages, setDamage]);

  return {
    damages: activeDamages,
    filteredDamages,
    loading: isLoading || isFetching || isManualFetching,
    error,
    fetchDamages,
  };
};
